import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Inbox } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 30_000;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0, total: 0 });
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  const load = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/inbox?limit=10");
      setData(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markAllRead = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/mark-read", { all: true });
      await load();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        className="relative w-9 h-9 grid place-items-center rounded-full border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4 text-slate-700" />
        {data.unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-ehi-accent text-white text-[10px] font-black grid place-items-center ring-2 ring-white tabular-nums"
            data-testid="notification-bell-badge"
          >
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50" data-testid="notification-bell-dropdown">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Notifications</div>
            {data.unread > 0 && (
              <button onClick={markAllRead} disabled={busy} className="text-[11px] font-bold text-ehi-primary hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="mark-all-read">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {(data.items || []).length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
              <Inbox className="w-8 h-8 text-slate-300" />
              You're all caught up
            </div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
              {data.items.map((it) => (
                <Link
                  key={it.id || `${it.req_id}-${it.kind}-${it.sent_at}`}
                  to="/dashboard/opportunities"
                  onClick={() => setOpen(false)}
                  className={`block px-4 py-3 hover:bg-slate-50 transition-colors ${!it.read_at ? "bg-orange-50/40" : ""}`}
                  data-testid={`notification-item-${it.kind}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-ehi-accent mb-0.5">
                        {(it.kind || "match").replace(/_/g, " ")}
                      </div>
                      <div className="text-sm text-slate-900 font-semibold truncate">
                        {it.subject || `New match · Req ${it.req_id?.slice(-6) || ""}`}
                      </div>
                      {it.email && <div className="text-[11px] text-slate-500 truncate">{it.email}</div>}
                    </div>
                    <div className="text-[10px] text-slate-400 whitespace-nowrap shrink-0">{timeAgo(it.sent_at)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-center">
            <Link to="/dashboard/opportunities" onClick={() => setOpen(false)} className="text-xs font-bold text-ehi-primary hover:underline">
              View all opportunities →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
